import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Camera, RotateCcw, Send, Loader2, ArrowLeft, Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { entryService } from "@/services/entry-service";

const ImageCapture = () => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Detect if we're on a mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const compressImage = async (file: File, maxWidth = 1920, maxHeight = 1080, quality = 0.8): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file); // Fallback to original if compression fails
          }
        }, 'image/jpeg', quality);
      };
      
      img.onerror = () => resolve(file); // Fallback to original if loading fails
      img.src = URL.createObjectURL(file);
    });
  };

  const startCamera = async () => {
    try {
      // Try with rear camera preference first (mobile-friendly)
      let constraints: MediaStreamConstraints = {
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        // Fallback to basic video if environment camera fails
        console.log("Environment camera failed, trying basic video:", err);
        constraints = { video: true };
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setCameraActive(true);
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      toast({
        title: "Camera access denied",
        description: "Please enable camera access to use this feature. On mobile, try the 'Upload Photo' option instead.",
        variant: "destructive",
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setCameraActive(false);
    }
  };

  const captureImage = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const imageDataUrl = canvas.toDataURL("image/jpeg");
        setImageUrl(imageDataUrl);
        stopCamera();
      }
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      setImageFile(file);
      stopCamera(); // Stop camera if it's active
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a valid image file",
        variant: "destructive",
      });
    }
  };

  const resetCapture = () => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(null);
    setImageFile(null);
    setCameraActive(false);
    stopCamera();
  };

  const handleSubmit = async () => {
    if (!imageUrl) return;

    setIsSubmitting(true);
    try {
      let file: File;
      
      if (imageFile) {
        // Compress uploaded file
        file = await compressImage(imageFile);
        
        toast({
          title: "Compressing image...",
          description: "Optimizing image for upload",
        });
      } else {
        // Convert canvas image to file (for camera captures)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageUrl;
        });
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.8);
        });
        
        file = new File([blob], 'captured-image.jpg', { type: 'image/jpeg' });
      }

      const result = await entryService.uploadAudio(file);
      
      toast({
        title: "Image submitted",
        description: "Your image has been analyzed successfully",
      });
      
      // Navigate to entry view page with the returned entry_id
      navigate(`/entry/${result.entry_id}`);
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Error submitting image",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-lg">
      <Button 
        variant="ghost" 
        className="mb-4 flex items-center gap-2"
        onClick={() => {
          stopCamera();
          navigate("/dashboard");
        }}
      >
        <ArrowLeft size={16} />
        <span>Back to Dashboard</span>
      </Button>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-center">Image Capture</CardTitle>
          <CardDescription className="text-center">
            Take a picture for analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center py-6">
          {!imageUrl && !cameraActive ? (
            <div className="flex flex-col items-center space-y-4 w-full">
              <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
                {isMobile ? (
                  <Button 
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex-1 bg-brand-500 hover:bg-brand-600 flex items-center gap-2 justify-center"
                  >
                    <Camera size={20} />
                    Take Photo
                  </Button>
                ) : (
                  <Button 
                    onClick={startCamera}
                    className="flex-1 bg-brand-500 hover:bg-brand-600 flex items-center gap-2 justify-center"
                  >
                    <Camera size={20} />
                    Take Photo
                  </Button>
                )}
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="flex-1 flex items-center gap-2 justify-center"
                >
                  <Upload size={20} />
                  Upload Photo
                </Button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
                className="hidden"
              />
              <p className="text-sm text-muted-foreground text-center">
                {isMobile 
                  ? "Take a photo with your camera or upload from your device. Camera button opens your device's camera app."
                  : "Take a photo with your camera or upload from your device"
                }
              </p>
            </div>
          ) : cameraActive ? (
            <div className="flex flex-col items-center space-y-4 w-full">
              <div className="relative w-full max-w-sm rounded-lg overflow-hidden border border-gray-200">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  className="w-full h-auto"
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      videoRef.current.play();
                    }
                  }}
                />
              </div>
              <Button 
                onClick={captureImage}
                className="capture-button bg-brand-500 hover:bg-brand-600 mt-4"
              >
                <Camera size={24} className="text-white" />
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-4 w-full">
              <div className="w-full max-w-sm rounded-lg overflow-hidden border border-gray-200">
                <img src={imageUrl} alt="Captured" className="w-full h-auto" />
              </div>
              <div className="flex gap-4 mt-4">
                <Button 
                  variant="outline" 
                  onClick={resetCapture}
                  className="flex items-center gap-2"
                >
                  <RotateCcw size={16} />
                  <span>Retake</span>
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-brand-500 hover:bg-brand-600 flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>Submit</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-center pb-6">
          <p className="text-sm text-muted-foreground text-center max-w-xs">
            {!cameraActive && !imageUrl ? 
              "Make sure you have good lighting for best results" : 
              cameraActive ? 
                "Position yourself in the frame and click to capture" : 
                "Review your image before submitting"
            }
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ImageCapture;
